import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { cn } from '@/lib/utils';

interface PopoverProps {
  trigger: ReactNode;
  children: ReactNode | ((close: () => void) => ReactNode);
  align?: 'start' | 'end';
  className?: string;
}

// Minimal click-outside popover. No focus trap — this is a hobbyist
// level of accessibility for a v1 dashboard. Escalate to Radix once
// we need keyboard-navigable menus or tooltips.
export function Popover({ trigger, children, align = 'start', className }: PopoverProps) {
  const [open, setOpen] = useState(false);
  const [effectiveAlign, setEffectiveAlign] = useState<'start' | 'end'>(align);
  const ref = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function handleEsc(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    window.addEventListener('mousedown', handleClick);
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('mousedown', handleClick);
      window.removeEventListener('keydown', handleEsc);
    };
  }, [open]);

  // Reset to the prop-requested side every time the popover opens, then
  // flip below if the chosen side overflows the viewport. Without the
  // reset, a popover that flipped once stays flipped forever even after
  // a layout change makes the original side fit.
  useLayoutEffect(() => {
    if (open) setEffectiveAlign(align);
  }, [open, align]);

  useLayoutEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const PAD = 8;
    if (effectiveAlign === 'end' && rect.left < PAD) {
      setEffectiveAlign('start');
    } else if (effectiveAlign === 'start' && rect.right > viewportWidth - PAD) {
      setEffectiveAlign('end');
    }
  }, [open, effectiveAlign, className]);

  return (
    <div ref={ref} className="relative inline-block">
      <button type="button" className="contents" onClick={() => setOpen((o) => !o)}>
        {trigger}
      </button>
      {open && (
        <div
          ref={panelRef}
          role="dialog"
          className={cn(
            'absolute z-50 mt-1 w-80 rounded-md border bg-background p-3 shadow-lg',
            effectiveAlign === 'end' ? 'right-0' : 'left-0',
            className,
          )}
        >
          {typeof children === 'function' ? children(() => setOpen(false)) : children}
        </div>
      )}
    </div>
  );
}
